import React from "react"
import { GetServerSideProps } from "next"
import { InstantSearch } from "react-instantsearch-dom"
import { findResultsState } from "react-instantsearch-dom/server"
import { InstantSearchProps } from "react-instantsearch-core"

import NotFoundPage from "pages/404"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"
import AlgoliaSearchUI from "components/Algolia/AlgoliaSearchUI"
import VirtualRefinements from "@secureo/common/components/Algolia/VirtualRefinements"
import SEOWithPaginationTitle from "components/Algolia/SEOWithPaginationTitle"

import useAlgoliaURLSync, { createSearchQueryParams } from "@secureo/common/hooks/useAlgoliaURLSync"

import { parseAlgoliaSSRRequest } from "@secureo/common/utils/algolia/getInitialSearchState"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchBrandCategory from "@secureo/common/utils/commerceTools/graphql/queries/fetchBrandCategory"
import getCategoryPreviewProps, {
	CategoryPageFindSearchStateProps,
} from "@secureo/common/utils/commerceTools/getCategoryPreviewProps"
import serializeJSON from "@secureo/common/utils/serializeJSON"

import searchClient from "@secureo/common/utils/algolia/searchClient"
import { brandPageFilters } from "config/filterConfig"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import getCountryCodeFromHostAndQueryParams from "utils/getCountryCodeFromHostAndQueryParams"

const ancestorBreadCrumbs: BreadCrumb[] = [
	{
		label: "Marken",
		href: `/marken/`,
	},
]

const BrandPage = (props: Props & InstantSearchProps) => {
	const {
		category: brandCategory,
		locale,
		countryCode,
		indexName,
		resultsState,
		initialSearchState = {},
		// eslint-disable-next-line
		onSearchParameters = () => {},
		virtualRefinements,
		slug,
		widgetsCollector,
	} = props

	const asPath = `/marken/${slug}`
	const [searchState, onSearchStateChange] = useAlgoliaURLSync(
		initialSearchState,
		"/marken/[slug]",
		asPath,
	)
	const showMasterVariantName =
		searchState.menu?.lockType === undefined || searchState.menu?.lockType === "Alle Anzeigen"

	if (!brandCategory) return <NotFoundPage countryCode={countryCode} />

	const { name, description, shortDescription, metaTitle, metaDescription, categoryImg } =
		brandCategory

	const currentPath: BreadCrumb = {
		label: name,
		href: `/marken/${slug}`,
	}

	return (
		<>
			<InstantSearch
				indexName={indexName}
				searchClient={searchClient}
				resultsState={resultsState}
				searchState={searchState}
				onSearchParameters={onSearchParameters}
				onSearchStateChange={onSearchStateChange}
				widgetsCollector={widgetsCollector}
				createURL={createSearchQueryParams}
			>
				<SEOWithPaginationTitle
					asPath={asPath}
					categoryName={name}
					pageType="category"
					title={metaTitle}
					description={metaDescription}
					countryCode={countryCode}
					searchState={searchState}
				/>
				<VirtualRefinements virtualRefinements={virtualRefinements} />
				<BreadCrumbs
					countryCode={countryCode}
					currentPath={currentPath}
					ancestors={ancestorBreadCrumbs}
				/>
				<AlgoliaSearchUI
					locale={locale}
					indexName={indexName}
					filters={brandPageFilters}
					title={name}
					shortDescription={shortDescription}
					description={description}
					virtualRefinements={virtualRefinements}
					categoryImg={categoryImg}
					showMasterVariantName={showMasterVariantName}
				/>
			</InstantSearch>
		</>
	)
}

interface Props extends CategoryPageFindSearchStateProps {
	resultsState: InstantSearchProps["resultsState"]
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { req, res, params, query } = context

	const host = req?.headers?.host ?? window?.location?.host
	const countryCode = getCountryCodeFromHostAndQueryParams(host, query) as CountryCode
	const language = "de"
	const locale = `${language}-${countryCode}`
	const { actualSlug, unsortedIndexName, searchState } = parseAlgoliaSSRRequest(
		req.url,
		params,
		"TRE",
		locale,
	)

	const accessToken = await anonymousAccessTokenCache()
	const brandCategory = await fetchBrandCategory(accessToken, actualSlug, locale, "TRE")
	if (!brandCategory) {
		res.statusCode = 404
		return {
			props: {
				statusCode: 404,
				error: "Category not found",
			},
		}
	}

	const previewProps = await getCategoryPreviewProps(
		brandCategory,
		searchState,
		unsortedIndexName,
		accessToken,
		locale,
		language,
		countryCode,
		"TRE",
		"brand",
	)

	let resultsState: InstantSearchProps["resultsState"] = {}
	try {
		resultsState = await findResultsState(
			(props: any) => <BrandPage {...previewProps} {...props} />,
			{
				indexName: previewProps.indexName,
				searchClient,
				searchState,
			},
		)
	} catch (err) {
		console.error(err)
		res.statusCode = 500
		return {
			props: {
				statusCode: 500,
				error: "Failed to create resultsState",
			},
		}
	}
	const serializedResultsState = serializeJSON(resultsState)

	const props: Props = {
		...previewProps,
		resultsState: serializedResultsState,
	}

	return {
		props,
	}
}

export default BrandPage
